import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { hierarchy, tree } from 'd3-hierarchy';
import { select } from 'd3-selection';
import { zoom, zoomIdentity } from 'd3-zoom';
import 'd3-transition';
import { Maximize, Minimize, Minus, Plus, RotateCcw } from 'lucide-react';
import { categoryTreeData, categoryTreePath, categoryTreeRadii, focusCamera, layoutTreeGeometry, nearestTreeNode, overviewCamera, treeZoomScale, TREE_NODE_GAP, TREE_ZOOM_STEPS } from '../../utils/categoryTree';
import './CategoryTree.css';

const COLORS = ['#005bbb', '#059669', '#be3455', '#07838b', '#93641c', '#52616c'];
const transformOf = ({ x, y, k }) => zoomIdentity.translate(x, y).scale(k);
const persian = (value) => Number(value).toLocaleString('fa-IR');

function radialLayout(categories, aspectRatio) {
  const root = hierarchy(categoryTreeData(categories));
  const radii = categoryTreeRadii(root.descendants());
  root.each((node) => { node.radius = radii[node.depth]; });
  const branches = (root.children || []).map((branch) => {
    tree().nodeSize([1, 260]).separation((a, b) => a.radius + b.radius + TREE_NODE_GAP)(branch);
    return branch.descendants().map((node) => ({ key: node.data.key, parentKey: node.parent.data.key, title: node.data.title, depth: node.depth, radius: node.radius, tangent: node.x }));
  });
  const geometry = layoutTreeGeometry(branches, root.radius, aspectRatio);
  root.each((node) => {
    const position = geometry.get(node.data.key);
    node.px = position.px;
    node.py = position.py;
    node.x = Math.atan2(node.px, -node.py);
    node.y = Math.hypot(node.px, node.py);
    const branch = node.ancestors().find((ancestor) => ancestor.depth === 1);
    node.color = branch ? COLORS[root.children.indexOf(branch) % COLORS.length] : '#005bbb';
  });
  return { root, nodes: root.descendants(), links: root.links(), maxRadius: radii[1] };
}

export default function CategoryTree({ categories }) {
  const hostRef = useRef(null);
  const svgRef = useRef(null);
  const engineRef = useRef(null);
  const cameraRef = useRef({ x: 0, y: 0, k: 1 });
  const historyRef = useRef([]);
  const stepRef = useRef(0);
  const busyRef = useRef(false);
  const pointerRef = useRef(null);
  const zoomActionRef = useRef(null);
  const touchRef = useRef(new Map());
  const pinchRef = useRef(null);
  const suppressClickRef = useRef(0);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const aspectRatio = size.width / Math.max(1, size.height);
  const layout = useMemo(() => radialLayout(categories, aspectRatio), [categories, aspectRatio]);
  const [camera, setCamera] = useState(cameraRef.current);
  const [step, setStep] = useState(0);
  const [focus, setFocus] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const overview = useMemo(() => overviewCamera(layout.nodes, size.width, size.height), [layout, size]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    const measure = () => {
      const { width, height } = host.getBoundingClientRect();
      setSize((old) => old.width === width && old.height === height ? old : { width, height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!size.width || !size.height) return undefined;
    const svg = select(svgRef.current);
    const engine = zoom().extent([[0, 0], [size.width, size.height]])
      .scaleExtent([overview.k, treeZoomScale(overview, size.width, size.height, TREE_ZOOM_STEPS - 1, layout.maxRadius)]).touchable(false).clickDistance(6)
      .interpolate((start, end) => (progress) => start.map((value, index) => value + (end[index] - value) * progress))
      .filter((event) => event.type !== 'wheel' && event.type !== 'dblclick' && !event.button && !busyRef.current)
      .on('start', (event) => { if (event.sourceEvent) setDragging(true); })
      .on('zoom', (event) => {
        const next = { x: event.transform.x, y: event.transform.y, k: event.transform.k };
        cameraRef.current = next;
        setCamera(next);
      })
      .on('end', () => setDragging(false));
    engineRef.current = engine;
    svg.call(engine).on('wheel.zoom', null).on('dblclick.zoom', null);
    svg.interrupt().call(engine.transform, transformOf(overview));
    stepRef.current = 0;
    historyRef.current = [];
    busyRef.current = false;
    pointerRef.current = null;
    setStep(0);
    setFocus(null);
    let wheelAmount = 0, lastWheel = 0;
    const wheel = (event) => {
      event.preventDefault();
      if (!event.deltaY || busyRef.current) return;
      const now = performance.now();
      if (now - lastWheel > 180 || Math.sign(event.deltaY) !== Math.sign(wheelAmount)) wheelAmount = 0;
      lastWheel = now;
      wheelAmount += event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? size.height : 1);
      if (Math.abs(wheelAmount) < 60) return;
      wheelAmount = 0;
      const rect = svgRef.current.getBoundingClientRect();
      zoomActionRef.current?.(event.deltaY < 0 ? 1 : -1, [event.clientX - rect.left, event.clientY - rect.top]);
    };
    const element = svgRef.current;
    element.addEventListener('wheel', wheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', wheel);
      svg.interrupt().on('.zoom', null);
      engineRef.current = null;
    };
  }, [layout.maxRadius, overview, size]);

  const animate = useCallback((next) => {
    const engine = engineRef.current;
    if (!engine) return;
    busyRef.current = true;
    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 480;
    select(svgRef.current).interrupt().transition().duration(duration)
      .call(engine.transform, transformOf(next))
      .on('end interrupt', () => { busyRef.current = false; });
  }, []);

  const changeZoom = useCallback((direction, point) => {
    if (busyRef.current) return;
    const currentStep = stepRef.current;
    if (direction < 0) {
      if (!currentStep) return;
      const previous = historyRef.current.pop();
      stepRef.current = currentStep - 1;
      setStep(currentStep - 1);
      setFocus(previous?.focus || null);
      animate(currentStep === 1 ? overview : previous.camera);
      return;
    }
    if (currentStep >= TREE_ZOOM_STEPS - 1) return;
    const target = !point && focus ? focus : nearestTreeNode(layout.nodes, point || pointerRef.current || [size.width / 2, size.height / 2], cameraRef.current);
    if (!target) return;
    historyRef.current.push({ camera: { ...cameraRef.current }, focus });
    const nextStep = currentStep + 1;
    stepRef.current = nextStep;
    setStep(nextStep);
    setFocus(target);
    animate(focusCamera(target, size.width, size.height, overview, nextStep, layout.maxRadius));
  }, [animate, focus, layout, overview, size]);
  zoomActionRef.current = changeZoom;

  function reset() {
    select(svgRef.current).interrupt();
    busyRef.current = false;
    stepRef.current = 0;
    historyRef.current = [];
    setStep(0);
    setFocus(null);
    animate(overview);
  }

  useEffect(() => {
    const update = () => setFullscreen(document.fullscreenElement === hostRef.current?.parentElement);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  function touchDown(event) {
    if (event.pointerType !== 'touch') return;
    touchRef.current.set(event.pointerId, [event.clientX, event.clientY]);
    if (touchRef.current.size === 2) {
      const [a, b] = [...touchRef.current.values()];
      pinchRef.current = Math.hypot(a[0] - b[0], a[1] - b[1]);
    }
  }

  function touchMove(event) {
    const rect = svgRef.current.getBoundingClientRect();
    pointerRef.current = [event.clientX - rect.left, event.clientY - rect.top];
    if (event.pointerType !== 'touch' || !touchRef.current.has(event.pointerId)) return;
    const previous = touchRef.current.get(event.pointerId);
    touchRef.current.set(event.pointerId, [event.clientX, event.clientY]);
    if (touchRef.current.size === 2) {
      const [a, b] = [...touchRef.current.values()];
      const distance = Math.hypot(a[0] - b[0], a[1] - b[1]);
      if (pinchRef.current && Math.abs(distance - pinchRef.current) > 35 && !busyRef.current) {
        svgRef.current.setPointerCapture(event.pointerId);
        suppressClickRef.current = Date.now() + 600;
        changeZoom(distance > pinchRef.current ? 1 : -1, [(a[0] + b[0]) / 2 - rect.left, (a[1] + b[1]) / 2 - rect.top]);
        pinchRef.current = distance;
      }
    } else if (!busyRef.current && engineRef.current) {
      if (Math.hypot(event.clientX - previous[0], event.clientY - previous[1]) < 2) return;
      svgRef.current.setPointerCapture(event.pointerId);
      suppressClickRef.current = Date.now() + 600;
      setDragging(true);
      const current = cameraRef.current;
      select(svgRef.current).call(engineRef.current.transform, transformOf({ ...current, x: current.x + event.clientX - previous[0], y: current.y + event.clientY - previous[1] }));
    }
  }

  function touchEnd(event) {
    touchRef.current.delete(event.pointerId);
    pinchRef.current = null;
    setDragging(false);
  }

  const focusKeys = useMemo(() => focus ? new Set([...focus.descendants(), ...focus.ancestors()].map((node) => node.data.key)) : null, [focus]);
  const strokeScale = Math.min(1, camera.k * 4);

  return (
    <section className="category-tree" aria-label="نمودار درختی محصولات">
      <div className="category-tree-toolbar" dir="rtl">
        <div className="category-tree-current" aria-live="polite">{focus?.data.title || 'همه محصولات'}</div>
        <div className="category-tree-controls" dir="ltr">
          <button type="button" title="کوچک‌نمایی" aria-label="کوچک‌نمایی" onClick={() => changeZoom(-1)} disabled={!step}><Minus size={18} /></button>
          <output aria-label="مرحله بزرگ‌نمایی">{persian(step + 1)} / {persian(TREE_ZOOM_STEPS)}</output>
          <button type="button" title="بزرگ‌نمایی" aria-label="بزرگ‌نمایی" onClick={() => changeZoom(1)} disabled={step === TREE_ZOOM_STEPS - 1}><Plus size={18} /></button>
          <button type="button" title="بازگشت به نمای اولیه" aria-label="بازگشت به نمای اولیه" onClick={reset}><RotateCcw size={18} /></button>
          {document.fullscreenEnabled && <button type="button" title={fullscreen ? 'خروج از تمام‌صفحه' : 'تمام‌صفحه'} aria-label={fullscreen ? 'خروج از تمام‌صفحه' : 'تمام‌صفحه'} onClick={() => { const action = fullscreen ? document.exitFullscreen() : hostRef.current.parentElement.requestFullscreen(); action?.catch(() => {}); }}>{fullscreen ? <Minimize size={18} /> : <Maximize size={18} />}</button>}
        </div>
      </div>
      <div className="category-tree-surface" ref={hostRef}>
        <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${size.width || 1} ${size.height || 1}`} role="group" aria-label="درخت دسته‌بندی محصولات" className={dragging ? 'is-dragging' : ''} style={{ '--tree-stroke-scale': strokeScale }} data-step={step} data-camera={`${camera.x},${camera.y},${camera.k}`} onPointerDown={touchDown} onPointerMove={touchMove} onPointerUp={touchEnd} onPointerCancel={touchEnd} onClickCapture={(event) => { if (dragging || Date.now() < suppressClickRef.current) { event.preventDefault(); event.stopPropagation(); } }}>
          <g transform={`translate(${camera.x},${camera.y}) scale(${camera.k})`}>
            {layout.links.map(({ source, target }) => {
              const middle = (source.y + target.y) / 2;
              const sourceAngle = source.depth ? source.x : target.x;
              const a = [Math.cos(sourceAngle - Math.PI / 2) * middle, Math.sin(sourceAngle - Math.PI / 2) * middle];
              const b = [Math.cos(target.x - Math.PI / 2) * middle, Math.sin(target.x - Math.PI / 2) * middle];
              return <path key={target.data.key} d={`M${source.px},${source.py} C${a[0]},${a[1]} ${b[0]},${b[1]} ${target.px},${target.py}`} stroke={target.color} strokeWidth={(focusKeys?.has(target.data.key) ? 2 : 1.5) * strokeScale} strokeOpacity={focusKeys && !focusKeys.has(target.data.key) ? 0.15 : 0.55} fill="none" vectorEffect="non-scaling-stroke" />;
            })}
            {layout.nodes.map((node) => {
              const root = !node.depth;
              const inBranch = !focusKeys || focusKeys.has(node.data.key);
              const radius = node.radius;
              const inner = radius * Math.SQRT2 - 18;
              return (
                <g key={node.data.key} transform={`translate(${node.px},${node.py})`} data-node-key={node.data.key} data-depth={node.depth} className="category-tree-node" opacity={inBranch || root ? 1 : 0.75}>
                  <Link to={root ? '/' : categoryTreePath(node.data)} aria-label={node.data.title}>
                    <title>{node.ancestors().reverse().map((ancestor) => ancestor.data.title).join(' / ')}</title>
                    <circle r={radius} fill="#fff" stroke={node.color} strokeWidth={(root || focus?.data.key === node.data.key ? 2.5 : 1.5) * strokeScale} vectorEffect="non-scaling-stroke" />
                    {root ? <image href="/images/parspiping-logo.svg" x={-radius * 0.8} y={-radius * 0.28} width={radius * 1.6} height={radius * 0.56} /> : <foreignObject x={-inner / 2} y={-inner / 2} width={inner} height={inner}><div xmlns="http://www.w3.org/1999/xhtml" className="category-tree-name" dir="rtl">{node.data.title}</div></foreignObject>}
                  </Link>
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </section>
  );
}
